/**
 * ImageCard Component
 *
 * Displays a single generated image with download and copy actions.
 */

import { useState, useMemo, memo } from 'react';
import { Download, Copy, Check, ExternalLink, ZoomIn, FolderOpen } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { getProxiedImageUrl, getImageDownloadUrl } from '../../services/imageGenerationAPI';
import { isElectronAvailable, saveMediaFile } from '../../utils/electronBridge';
import type { ImageResult } from '../../types';
import { TIMEOUTS } from '../../constants/timings';

/** Read a Blob as a `data:` URL (base64) for the desktop save bridge. */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read image data'));
    reader.readAsDataURL(blob);
  });
}

interface ImageCardProps {
  image: ImageResult;
  index: number;
  onZoom?: (imageUrl: string) => void;
}

export const ImageCard = memo(function ImageCard({ image, index, onZoom }: ImageCardProps) {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const isDesktop = isElectronAvailable();

  // Get proxied URL for RunPod S3 images (handles auth)
  const displayUrl = useMemo(() => getProxiedImageUrl(image.url), [image.url]);

  // Resolve the image to a `data:` URL (base64). Local providers already return
  // one; cloud (RunPod S3) images are fetched through the CORS-safe proxy.
  const resolveImageDataUrl = async (): Promise<string> => {
    if (image.url.startsWith('data:')) {
      return image.url;
    }
    const response = await fetch(getImageDownloadUrl(image.url));
    if (!response.ok) {
      throw new Error(`Download proxy returned ${response.status}`);
    }
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.startsWith('image/')) {
      throw new Error(`Unexpected content-type from proxy: ${contentType}`);
    }
    return blobToDataUrl(await response.blob());
  };

  // Desktop: save to disk via native dialog + reveal in the file manager (there
  // is no browser download in Electron). Web: trigger a normal blob download.
  const handleDownload = async () => {
    const filename = `generated-image-${index + 1}-seed-${image.seed}.png`;

    if (isDesktop) {
      try {
        const dataUrl = await resolveImageDataUrl();
        const result = await saveMediaFile(dataUrl, filename);
        if (!result.success && !result.canceled) {
          throw new Error(result.error ?? 'Save failed');
        }
      } catch (err) {
        console.error('[ImageCard] Desktop save failed', err);
      }
      return;
    }

    try {
      const dataUrl = await resolveImageDataUrl();
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('[ImageCard] Download failed, opening original URL', err);
      window.open(displayUrl, '_blank');
    }
  };

  const handleCopyUrl = async () => {
    try {
      // Copy original URL (not proxied) for sharing
      await navigator.clipboard.writeText(image.url);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), TIMEOUTS.COPY_FEEDBACK);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  const handleOpenInNewTab = () => {
    window.open(displayUrl, '_blank');
  };

  return (
    <div className="group relative bg-black/30 rounded-lg border border-white/10 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Image Container */}
      <div className="relative aspect-square bg-black/20">
        {!isImageLoaded && (
          <div className="overlay-center">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        <img
          src={displayUrl}
          alt={t('image.result.imageAlt', {
            defaultValue: 'Generated image {{number}}',
            number: index + 1,
          })}
          className={`w-full h-full object-cover transition-opacity ${
            isImageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setIsImageLoaded(true)}
          loading="lazy"
        />

        {/* Zoom Overlay */}
        {isImageLoaded && onZoom && (
          <button
            type="button"
            onClick={() => onZoom(displayUrl)}
            className="absolute inset-0 bg-black/0 hover:bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
          >
            <ZoomIn className="h-8 w-8 text-white drop-shadow-lg" />
          </button>
        )}
      </div>

      {/* Image Info */}
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-white/50">
            {t('image.result.seed', { defaultValue: 'Seed' })}: {image.seed}
          </span>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleDownload}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {isDesktop ? <FolderOpen className="h-4 w-4" /> : <Download className="h-4 w-4" />}
            {isDesktop
              ? t('image.result.saveToFolder', { defaultValue: 'Save to folder' })
              : t('common.download', { defaultValue: 'Download' })}
          </button>
          <button
            type="button"
            onClick={handleCopyUrl}
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            title={t('image.result.copyUrl', { defaultValue: 'Copy URL' })}
          >
            {isCopied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={handleOpenInNewTab}
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            title={t('image.result.openNew', { defaultValue: 'Open in new tab' })}
          >
            <ExternalLink className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
});

export default ImageCard;
